import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const TIER_TO_SUBSCRIPTION: Record<string, 'free' | 'premium' | 'pro'> = {
  'premium-pickleball': 'premium',
  'premium-tennis': 'premium',
  'premium-golf': 'premium',
  'pro': 'pro',
};

type Status = 'loading' | 'success' | 'error';

const PaymentSuccessScreen: React.FC = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [tierName, setTierName] = useState('');
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');

    if (!sessionId) {
      setStatus('error');
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(`/api/verify-session?session_id=${sessionId}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setStatus('error');
          return;
        }

        const subscription = TIER_TO_SUBSCRIPTION[data.tierId] ?? 'premium';
        updateUser({ subscription });
        setTierName(data.tierId);
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };

    verify();
  }, [updateUser]);

  if (status === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.spinner}>⏳</div>
        <p>Confirming your payment...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.icon}>❌</div>
        <h2>Payment verification failed</h2>
        <p>Your card was not charged. Please try again or contact support.</p>
        <button style={styles.button} onClick={() => navigate('/pricing')}>
          Back to Pricing
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.icon}>🎉</div>
      <h2>You're all set!</h2>
      <p>Your <strong>{tierName}</strong> subscription is now active.</p>
      <button style={styles.button} onClick={() => navigate('/')}>
        Start Coaching
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: '1rem',
    padding: '2rem',
    textAlign: 'center',
  },
  icon: { fontSize: '4rem' },
  spinner: { fontSize: '3rem' },
  button: {
    marginTop: '1rem',
    padding: '0.75rem 2rem',
    backgroundColor: '#FF6B35',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    cursor: 'pointer',
  },
};

export default PaymentSuccessScreen;
